import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiZoomIn, FiZoomOut, FiRefreshCw } from 'react-icons/fi';

const ImagePreviewWithZoom = ({ src, alt }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);
  
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  
  // ドラッグ開始
  const handleMouseDown = (e) => {
    if (scale <= 1) return; // ズームインしていない場合はドラッグ無効
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };
  
  // ドラッグ中
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // コンテナのサイズを取得
    const container = containerRef.current;
    const img = imgRef.current;
    
    if (!container || !img) return;
    
    // 画像が表示領域から出ないように制限
    const containerRect = container.getBoundingClientRect();
    const imgWidth = img.width * scale;
    const imgHeight = img.height * scale;
    
    const maxX = Math.max(0, (imgWidth - containerRect.width) / 2);
    const maxY = Math.max(0, (imgHeight - containerRect.height) / 2);
    
    const boundedX = Math.min(Math.max(newX, -maxX), maxX);
    const boundedY = Math.min(Math.max(newY, -maxY), maxY);
    
    setPosition({ x: boundedX, y: boundedY });
  };
  
  // ドラッグ終了
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // 2点間の距離を計算
  const getDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // タッチイベント対応
  const handleTouchStart = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // ピンチズーム開始
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialScale(scale);
      setIsDragging(false);
    } else if (e.touches.length === 1 && scale > 1) {
      // ドラッグ開始
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };
  
  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2) {
      // ピンチズーム
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scaleRatio = currentDistance / initialPinchDistance;
      const newScale = Math.min(Math.max(initialScale * scaleRatio, 1), 10);
      
      setScale(newScale);
    } else if (e.touches.length === 1 && isDragging) {
      // ドラッグ
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      
      // コンテナのサイズを取得
      const container = containerRef.current;
      const img = imgRef.current;
      
      if (!container || !img) return;
      
      // 画像が表示領域から出ないように制限
      const containerRect = container.getBoundingClientRect();
      const imgWidth = img.width * scale;
      const imgHeight = img.height * scale;
      
      const maxX = Math.max(0, (imgWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (imgHeight - containerRect.height) / 2);
      
      const boundedX = Math.min(Math.max(newX, -maxX), maxX);
      const boundedY = Math.min(Math.max(newY, -maxY), maxY);
      
      setPosition({ x: boundedX, y: boundedY });
    }
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
    setInitialPinchDistance(0);
  };
  
  // ズームイン
  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.25, 10));
  };
  
  // ズームアウト
  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.25, 1));
  };

  // ホイール（ピンチ）によるズーム
  const handleWheel = (e) => {
    e.preventDefault();
    
    const container = containerRef.current;
    if (!container) return;

    // マウス位置を取得
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // 中心からの相対位置
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const offsetX = mouseX - centerX;
    const offsetY = mouseY - centerY;

    // ズーム前の位置を計算
    const prevScale = scale;
    let newScale;
    
    const SCALE_STEP = 0.1; // より細かいステップでスムーズに
    const MIN_SCALE = 1;
    const MAX_SCALE = 10;
    
    // ホイールの方向でズーム
    if (e.deltaY < 0) {
      // 上にスクロール = ズームイン
      newScale = Math.min(prevScale + SCALE_STEP, MAX_SCALE);
    } else {
      // 下にスクロール = ズームアウト
      newScale = Math.max(prevScale - SCALE_STEP, MIN_SCALE);
    }
    
    // スケールが変更される場合のみ位置を調整
    if (newScale !== prevScale) {
      // ズーム中心をマウス位置に合わせるための位置調整
      const scaleRatio = newScale / prevScale;
      const newX = position.x - offsetX * (scaleRatio - 1);
      const newY = position.y - offsetY * (scaleRatio - 1);
      
      setScale(newScale);
      setPosition({ x: newX, y: newY });
    }
  };
  
  // リセット
  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };
  
  // スケール変更時に位置をリセット
  useEffect(() => {
    if (scale === 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);
  
  // マウスイベントのクリーンアップ
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);
    
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, []);

  // ホイールイベントの登録
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [scale, position]);

  return (
    <Box>
      <HStack spacing={4} mb={2}>
        <IconButton
          icon={<FiZoomOut />}
          onClick={handleZoomOut}
          aria-label="縮小"
          size="sm"
          isDisabled={scale <= 1}
        />
        <Slider
          value={scale}
          min={1}
          max={10}
          step={0.25}
          onChange={(v) => setScale(v)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          flex="1"
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <Tooltip
            hasArrow
            bg="brand.500"
            color="white"
            placement="top"
            isOpen={showTooltip}
            label={`${Math.round(scale * 100)}%`}
          >
            <SliderThumb />
          </Tooltip>
        </Slider>
        <IconButton
          icon={<FiZoomIn />}
          onClick={handleZoomIn}
          aria-label="拡大"
          size="sm"
          isDisabled={scale >= 10}
        />
        <IconButton
          icon={<FiRefreshCw />}
          onClick={handleReset}
          aria-label="リセット"
          size="sm"
        />
      </HStack>
      
      <Box
        ref={containerRef}
        position="relative"
        height="800px"
        width="100%"
        overflow="hidden"
        borderRadius="md"
        bg={useColorModeValue("gray.100", "gray.700")}
        cursor={scale > 1 ? "move" : "default"}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Box
          as="img"
          ref={imgRef}
          src={src}
          alt={alt}
          position="absolute"
          top="50%"
          left="50%"
          transform={`translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`}
          maxH="100%"
          maxW="100%"
          transformOrigin="center"
          transition={isDragging ? "none" : "transform 0.1s ease-out"}
          draggable="false"
          userSelect="none"
        />
      </Box>
    </Box>
  );
};

export default ImagePreviewWithZoom;